import React, { useEffect, useState } from "react";
import "./index.css";
import { Button, Form, Input, notification, Switch, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useForm } from "antd/es/form/Form";
import {
  getUser,
  updateUserWithNotification,
} from "../../../Services/UserProfileServices";
import CommonLayout from "../CommonLayout";
import User_Profile_Header from "../../../Assets/user_profile_header.png";
import { UserRespose } from "../../../redux/Reducer/userReducer";
import { userInformation } from "../../../redux/actions";
import store from "../../../redux/store";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined,
  MobileOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";
import { translation } from "../../../i18n/translationConstant";
import { useFingerprintValidation } from "../../Authentication/FingerprintValidationContext";

const UserProfile: React.FC<{
  setReduxUser: (user: any) => void;
}> = ({ setReduxUser }) => {
  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    vuaId: "",
    email: "",
    sendNotification: false,
  });

  const [changedValues, setChangedValues] = useState({
    firstName: "",
    lastName: "",
  });
  const [isProfileChanged, setIsProfileChanged] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [showVuaId, setShowVuaId] = useState(false);
  const [showMobileNumber, setShowMobileNumber] = useState(false);
  const { t } = useTranslation();
  const [form] = useForm();
  const { checkFingerprint } = useFingerprintValidation();

  useEffect(() => {
    getUser().then((res) => setUser(res.data));
    checkFingerprint();
  }, [checkFingerprint]);

  useEffect(() => {
    setChangedValues({
      firstName: user.firstName,
      lastName: user.lastName,
    });

    form.setFieldsValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    });
  }, [user, form]);

  const updateUserInfo = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsProfileChanged(true);

    if (event.target.id === "firstName") {
      setChangedValues((old) => ({ ...old, firstName: event.target.value }));
    } else {
      setChangedValues((old) => ({ ...old, lastName: event.target.value }));
    }
  };

  const onNameChange = (e: any) => {
    e.target.value = e.target.value.replace(/[^A-Za-z]+/g, "");
  };

  const updateUserData = (values: any) => {
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: user.email,
      // sendNotification: user.sendNotification,
    };

    updateUserWithNotification(payload).then(() => {
      notification.success({
        message: t("msgUserProfileSuccess"),
      });

      setReduxUser({
        firstName: changedValues.firstName,
        lastName: changedValues.lastName,
        mobileNumber: user.mobileNumber,
        vuaId: user.vuaId,
        email: user.email,
        sendNotification: user.sendNotification,
      });

      setIsProfileChanged(false);
    });
  };

  return (
    <CommonLayout
      title={t(translation.lblUserProfile)}
      showBackButton={true}
    >
      <div className="profileContent">
        <div className="profileIntro">
          <div>
            <Typography.Text className="profileIntroText">
              Keep your details up to date. You can change your name and email address.
            </Typography.Text>
          </div>
          <div className="profileSecureBadge">
            <LockOutlined />
            <span>Your information is secure</span>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          name="userProfileForm"
          autoComplete="off"
          scrollToFirstError={true}
          onFinish={updateUserData}
          className="profileForm"
        >
          <section className="profileSection identitySection" aria-labelledby="profileIdentityHeading">
            <div className="identityBlock">
              <div className="avatarWrap">
                <img
                  className="userProfileImage"
                  src={User_Profile_Header}
                  alt="Profile illustration"
                />
              </div>
              <div className="identityText">
                <Typography.Title level={4} id="profileIdentityHeading">
                  {`${user.firstName || ""} ${user.lastName || ""}`.trim() || "Your profile"}
                </Typography.Title>
                <Typography.Text>
                  Account details and notification preferences
                </Typography.Text>
              </div>
            </div>
          </section>

          <section className="profileSection" aria-labelledby="personalDetailsHeading">
            <div className="sectionHeading">
              <div>
                <Typography.Title level={4} id="personalDetailsHeading">
                  Personal information
                </Typography.Title>
                <Typography.Text>
                  These details are used to identify your account.
                </Typography.Text>
              </div>
            </div>

            <div className="profileGrid">
              <Form.Item
                className="profileFormItem"
                name="firstName"
                label={t("lblFirstName")}
                rules={[{ required: true, message: t("errorRequiredFirstName") }]}
              >
                <Input
                  id="firstName"
                  className="formInput"
                  value={changedValues.firstName}
                  prefix={<UserOutlined />}
                  onInput={onNameChange}
                  onChange={updateUserInfo}
                  aria-label={t("lblFirstName")}
                />
              </Form.Item>

              <Form.Item
                className="profileFormItem"
                name="lastName"
                label={t("lblLastName")}
                rules={[{ required: true, message: t("errorRequiredLastName") }]}
              >
                <Input
                  id="lastName"
                  className="formInput"
                  value={changedValues.lastName}
                  prefix={<UserOutlined />}
                  onInput={onNameChange}
                  onChange={updateUserInfo}
                  aria-label={t("lblLastName")}
                />
              </Form.Item>

              <Form.Item className="profileFormItem" label={t("lblUserName")}>
                <Input
                  id="vuaid"
                  value={user.vuaId}
                  type={showVuaId ? "text" : "password"}
                  readOnly
                  className="formInput readonlyInput"
                  prefix={<LockOutlined />}
                  suffix={
                    <button
                      type="button"
                      className="visibilityButton"
                      onClick={() => setShowVuaId(!showVuaId)}
                      aria-label={showVuaId ? "Hide user name" : "Show user name"}
                    >
                      {showVuaId ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    </button>
                  }
                />
                <Typography.Text className="fieldHint">
                  This identifier cannot be changed.
                </Typography.Text>
              </Form.Item>

              <Form.Item className="profileFormItem" label={t("lblMobileNumber")}>
                <Input
                  id="mobileNumber"
                  value={user.mobileNumber}
                  type={showMobileNumber ? "text" : "password"}
                  readOnly
                  className="formInput readonlyInput"
                  prefix={<MobileOutlined />}
                  suffix={
                    <button
                      type="button"
                      className="visibilityButton"
                      onClick={() => setShowMobileNumber(!showMobileNumber)}
                      aria-label={showMobileNumber ? "Hide mobile number" : "Show mobile number"}
                    >
                      {showMobileNumber ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    </button>
                  }
                />
                <Typography.Text className="fieldHint">
                  Your registered mobile number cannot be changed here.
                </Typography.Text>
              </Form.Item>

              <Form.Item
                className="profileFormItem fullWidth"
                name="email"
                label={t("lblEmail")}
                rules={[
                  { type: "email", message: t("lblInvalidEmail") },
                  { max: 100, message: t("lblEmailTooLong") },
                ]}
              >
                <Input
                  id="email"
                  value={user.email}
                  placeholder={t("lblEnterEmail")}
                  prefix={<MailOutlined />}
                  className={`formInput ${emailError ? "emailErrorInput" : ""}`}
                  onChange={(e) => {
                    setIsProfileChanged(true);
                    const value = e.target.value;

                    setUser((prev) => ({ ...prev, email: value }));
                    form.setFieldValue("email", value);

                    if (value.trim()) setEmailError(false);
                  }}
                  aria-label={t("lblEmail")}
                />
                {emailError && (
                  <Typography.Text className="emailErrorText">
                    Please add an email address to enable monthly notifications.
                  </Typography.Text>
                )}
              </Form.Item>
            </div>
          </section>

          {/* <section className="profileSection notificationSection" aria-labelledby="notificationHeading">
            <div className="sectionHeading">
              <div>
                <Typography.Title level={4} id="notificationHeading">
                  {t("iblNotifications")}
                </Typography.Title>
                <Typography.Text>
                  Choose whether you want to receive a monthly summary by email.
                </Typography.Text>
              </div>
            </div>

            <div className="notificationCard">
              <div className="notificationLeft">
                <div className="notificationIcon" aria-hidden="true">
                  <MailOutlined />
                </div>
                <div className="notificationContent">
                  <Typography.Text className="summaryTitle">
                    {t("iblMonthlyUpdate")}
                  </Typography.Text>
                  <Typography.Text className="summaryDesc">
                    {t("iblMonthlyUpdateRecive")}
                  </Typography.Text>
                </div>
              </div>

              <Switch
                checked={user.sendNotification}
                onChange={(checked) => {
                  if (!user.email?.trim()) {
                    setEmailError(true);
                    return;
                  }

                  setEmailError(false);
                  setIsProfileChanged(true);
                  setUser((prev) => ({ ...prev, sendNotification: checked }));
                }}
                aria-label="Monthly summary notifications"
              />
            </div>
          </section> */}

          <div className="profileActions">
            <Typography.Text className="saveHint">
              {isProfileChanged ? "You have unsaved changes." : "All changes are saved."}
            </Typography.Text>
            <Button
              htmlType="submit"
              className="profileSubmitButton"
              disabled={!isProfileChanged}
            >
              {t("lblUpdateDetails")}
            </Button>
          </div>
        </Form>
      </div>
    </CommonLayout>
  );
};

const mapDispatchToProps = () => {
  return {
    setReduxUser: (changedUser: UserRespose) =>
      store.dispatch(userInformation(changedUser)),
  };
};

export default connect(null, mapDispatchToProps)(UserProfile);
